'use client'

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { Category } from '@shared/types/marketplace'
import { apiClient } from '../lib/api'
import { useAuth } from './AuthContext'

interface CategoriesContextType {
  serviceParentCategories: Category[]
  loading: boolean
  error: string | null
  getCategoryById: (id: string) => Category | undefined
  getServiceSubcategoriesByParent: (parentId: string) => Category[]
  refreshCategories: () => Promise<void>
  isInitialized: boolean
}

const CategoriesContext = createContext<CategoriesContextType | undefined>(undefined)

export const useCategories = () => {
  const context = useContext(CategoriesContext)
  if (context === undefined) {
    throw new Error('useCategories must be used within a CategoriesProvider')
  }
  return context
}

interface CategoriesProviderProps {
  children: ReactNode
}

export const CategoriesProvider: React.FC<CategoriesProviderProps> = ({ children }) => {
  const { user } = useAuth()
  const [serviceCategories, setServiceCategories] = useState<Category[]>([])
  
  // Service categories by level
  const [serviceParentCategories, setServiceParentCategories] = useState<Category[]>([])
  const [serviceSubcategoriesMap, setServiceSubcategoriesMap] = useState<Map<string, Category[]>>(new Map())
  
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)

  // Only fetch categories when user is logged in
  useEffect(() => {
    if (user && !isInitialized) {
      fetchCategories()
    }
  }, [user, isInitialized])

  const fetchCategories = async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch only service categories
      const serviceResponse = await apiClient.get<any>('/categories?type=service')
      const serviceCats = (serviceResponse.data as any).success ? (serviceResponse.data as any).data : []

      // Set main category array
      setServiceCategories(serviceCats)

      // Separate service categories by level
      const serviceParents = serviceCats.filter((cat: Category) => cat.level === 0)
      const serviceSubcats = serviceCats.filter((cat: Category) => cat.level === 1)
      
      setServiceParentCategories(serviceParents)
      
      // Build service subcategories map (parentId -> subcategories[])
      const serviceSubcatsMap = new Map<string, Category[]>()
      serviceSubcats.forEach((subcat: Category) => {
        if (subcat.parentId) {
          const existing = serviceSubcatsMap.get(subcat.parentId) || []
          serviceSubcatsMap.set(subcat.parentId, [...existing, subcat])
        }
      })
      setServiceSubcategoriesMap(serviceSubcatsMap)
      
      setIsInitialized(true)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch categories'
      setError(errorMessage)
      console.error('Failed to fetch categories:', err)
    } finally {
      setLoading(false)
    }
  }

  const getCategoryById = (id: string): Category | undefined => {
    return serviceCategories.find(cat => cat.id === id)
  }

  const getServiceSubcategoriesByParent = (parentId: string): Category[] => {
    return serviceSubcategoriesMap.get(parentId) || []
  }

  const refreshCategories = async () => {
    await fetchCategories()
  }

  const value: CategoriesContextType = {
    serviceParentCategories,
    loading,
    error,
    getCategoryById,
    getServiceSubcategoriesByParent,
    refreshCategories,
    isInitialized
  }

  return (
    <CategoriesContext.Provider value={value}>
      {children}
    </CategoriesContext.Provider>
  )
}
